import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, User, Menu, X, Mail } from "lucide-react";
import GlobalSearchModal from "./GlobalSearchModal";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";
import { AdminReorder } from "../admin/AdminControls";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Our Story", href: "/our-story" },
  { label: "Experiences", href: "/experiences" },
  { label: "Deals", href: "/deals" },
  { label: "Blog", href: "/blog" },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  // Admin drag-reorder for the nav links (order persists + reflects publicly).
  const navMc = useManagedCollection<{ label: string; href: string }>(
    "header_nav",
    navLinks,
    (l) => slugify(l.label)
  );

  // Active = current route only (Home matches exactly; others match by prefix).
  const isActive = (href: string) =>
    href !== "#" && (href === "/" ? location.pathname === "/" : location.pathname.startsWith(href));

  return (
    <header className="w-full bg-white border-b border-gray-100">
      <div className="page-container h-[52px] sm:h-[56px] flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0">
          <img
            src="https://storage.googleapis.com/happyspace-preview-projects/uploads/projects/aac10704-c5ad-4b55-a2c3-0c294889f163/1784770864829-dt7lq8.png"
            alt="Dallas Desi Scoop"
            className="h-8 sm:h-9 w-auto object-contain"
          />
          {/* Brand Text */}
          <span className="text-[18px] sm:text-[22px] font-bold tracking-tight leading-none whitespace-nowrap">
            <span className="text-[#082f6d]">Dallas</span>
            <span className="text-[#f59e0b]">Desi</span>
            <span className="text-[#51ba4f]">Scoop</span>
          </span>
        </Link>

        {/* Desktop Navigation & Add Buttons */}
        <div className="hidden md:flex items-center gap-6">
          <nav className="flex items-center gap-6">
            {navMc.items.map((link) => (
              <AdminReorder key={link._key} mc={navMc} item={link}>
                <Link
                  to={link.href}
                  className={`relative text-[13px] font-semibold transition-colors duration-200 pb-1 ${
                    isActive(link.href) ? "text-[#082f6d]" : "text-gray-700 hover:text-[#082f6d]"
                  }`}
                >
                  {link.label}
                  {isActive(link.href) && (
                    <span className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-[#f59e0b] rounded-full" />
                  )}
                </Link>
              </AdminReorder>
            ))}
          </nav>

          <div className="flex items-center gap-2.5 border-l border-gray-200 pl-5">
            <Link
              to="/add-event"
              className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 shrink-0"
            >
              Add Event
            </Link>
            <Link
              to="/add-business"
              className="bg-[#f97316] hover:bg-[#ea580c] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 shrink-0"
            >
              Add Business
            </Link>
          </div>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center gap-3">
          {/* Contact Us Button */}
          <button
            onClick={() => setSearchOpen(true)}
            className="p-1.5 hover:bg-gray-50 rounded-md transition-colors duration-200 shrink-0 mr-1"
            title="Search"
          >
            <Search className="w-[18px] h-[18px] text-[#082f6d]" strokeWidth={2} />
          </button>

          <Link
            to="/contact"
            className="bg-[#51ba4f] hover:bg-[#43a041] text-white text-[11px] sm:text-[12px] font-bold px-2.5 sm:px-3 py-1.5 rounded-md transition-colors duration-200 shrink-0 flex items-center gap-1.5"
          >
            <Mail className="w-3.5 h-3.5" />
            <span>Contact Us</span>
          </Link>

          {/* User Icon */}
          <button
            onClick={() => navigate("/admin/login")}
            className="hidden md:block p-1.5 hover:bg-gray-50 rounded-md transition-colors duration-200 shrink-0"
            title="Admin Login"
          >
            <User className="w-[18px] h-[18px] text-[#082f6d]" strokeWidth={2} />
          </button>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden p-1.5 hover:bg-gray-50 rounded-md transition-colors duration-200 shrink-0"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-5 h-5 text-[#082f6d]" strokeWidth={2} />
            ) : (
              <Menu className="w-5 h-5 text-[#082f6d]" strokeWidth={2} />
            )}
          </button>
        </div>
      </div>

        {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white">
          <div className="page-container py-3 flex flex-col gap-1">
            {navMc.items.map((link) => (
              <Link
                key={link._key}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`text-[14px] font-semibold py-2.5 px-3 rounded-md transition-colors duration-200 ${
                  isActive(link.href)
                    ? "text-[#082f6d] bg-orange-50"
                    : "text-gray-700 hover:text-[#082f6d] hover:bg-gray-50"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/add-event"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-2 bg-[#f97316] hover:bg-[#ea580c] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 w-full text-center"
            >
              Add Event
            </Link>
            <Link
              to="/add-business"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-1.5 bg-[#f97316] hover:bg-[#ea580c] text-white text-[12px] font-bold px-3 py-1.5 rounded-md transition-colors duration-200 w-full text-center"
            >
              Add Business
            </Link>
          </div>
        </div>
      )}

      {/* Global Search Modal */}
      <GlobalSearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
